"use client";

/**
 * FeedbackFab — 모든 페이지 우하단 💬 플로팅 버튼.
 * 탭하면 시트가 올라와 메모/스크린샷 전송. /api/feedback 으로 POST.
 *
 * - 로그인 안 했으면 숨김.
 * - /login 에선 숨김.
 * - BottomNav 가 떠있는 페이지에선 그 위로 살짝 띄움.
 */
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { Drawer } from "vaul";
import { ImagePlus, Loader2, MessageSquarePlus, X } from "lucide-react";
import { useAuth } from "./auth-provider";
import { useToast } from "./toast-provider";
import { cn } from "@/lib/utils";

export function FeedbackFab() {
  const { user, loading } = useAuth();
  const pathname = usePathname() || "/";
  const [open, setOpen] = useState(false);

  // 안 보여야 하는 페이지
  const hideRoutes = ["/login"];
  const hidden =
    loading ||
    !user ||
    hideRoutes.some((p) => pathname === p || pathname.startsWith(p + "/")) ||
    pathname === "/budget/add"; // 입력 페이지는 키보드와 충돌 → 숨김

  if (hidden) return null;

  // BottomNav가 보이는 라우트면 위로 더 띄움
  const onBudget = pathname.startsWith("/budget") && pathname !== "/budget/add";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="제보하기"
        className={cn(
          "fixed right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full",
          "bg-[var(--accent)] text-white shadow-lg shadow-[var(--accent)]/30",
          "transition active:scale-95",
        )}
        style={{
          bottom: onBudget
            ? "calc(env(safe-area-inset-bottom) + 88px)"
            : "calc(env(safe-area-inset-bottom) + 24px)",
        }}
      >
        <MessageSquarePlus size={22} strokeWidth={2.4} />
      </button>

      <FeedbackSheet open={open} onOpenChange={setOpen} page={pathname} />
    </>
  );
}

function FeedbackSheet({
  open,
  onOpenChange,
  page,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  page: string;
}) {
  const { show } = useToast();
  const [message, setMessage] = useState("");
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setMessage("");
      setScreenshotUrl(null);
      setError(null);
    }
  }, [open]);

  const canSend = message.trim().length > 0 && !saving;

  const handleFile = async (f: File) => {
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", f);
      const r = await fetch("/api/upload", { method: "POST", body: form });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || "업로드 실패");
      }
      const { url } = await r.json();
      setScreenshotUrl(url);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "업로드 실패");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const send = async () => {
    if (!canSend) return;
    setSaving(true);
    setError(null);
    try {
      const r = await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: message.trim(),
          page,
          screenshotPath: screenshotUrl,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || "전송 실패");
      }
      onOpenChange(false);
      show({
        kind: "success",
        message: "받았어, 자비스가 PM에게 전달했어 💬",
      });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "전송 실패");
    } finally {
      setSaving(false);
    }
  };

  // 라운드 92 (2026-04-28): 모바일 키보드 가림 방지.
  // iOS의 vh는 키보드 켜져도 안 줄어서 시트가 키보드 뒤로 밀려남 → dvh로 교체.
  // textarea 포커스 시 사용자가 입력창을 볼 수 있도록 scrollIntoView 보강.
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 flex max-h-[92dvh] flex-col rounded-t-3xl bg-[var(--surface)] outline-none">
          <Drawer.Title className="sr-only">제보하기</Drawer.Title>
          <div className="mx-auto mt-3 h-1.5 w-10 rounded-full bg-[var(--border)]" />
          <div className="flex items-center justify-between px-5 pt-4 pb-2">
            <div className="text-lg font-semibold">제보하기</div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="닫기"
              className="flex h-9 w-9 items-center justify-center rounded-full text-[var(--muted)] active:bg-[var(--surface-2)] active:text-[var(--foreground)]"
            >
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 pb-4">
            <div className="mb-3 rounded-xl bg-[var(--surface-2)] px-3 py-2 text-xs text-[var(--muted)]">
              현재 페이지: <span className="text-[var(--foreground)]">{page}</span>
            </div>

            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onFocus={() => {
                // 키보드 올라온 뒤에 입력창 보이도록 살짝 지연 후 스크롤.
                // setTimeout 안에서 e.currentTarget 쓰면 React event pool로 인해 null.
                // ref로 직접 참조해야 안전.
                setTimeout(() => {
                  textareaRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
                }, 250);
              }}
              placeholder="여기 이상해. 이런 게 있으면 좋겠어. 등등…"
              rows={5}
              maxLength={2000}
              className="w-full resize-none rounded-2xl bg-[var(--surface-2)] px-4 py-3 text-sm outline-none placeholder:text-[var(--muted)]"
            />
            <div className="mt-1 text-right text-[11px] text-[var(--muted)]">{message.length}/2000</div>

            <div className="mt-3">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif,image/heic,image/heif"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }}
              />
              {screenshotUrl ? (
                <div className="flex items-center gap-3 rounded-2xl bg-[var(--surface-2)] p-2">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={screenshotUrl}
                    alt="첨부 이미지"
                    className="h-16 w-16 rounded-xl object-cover"
                  />
                  <div className="flex-1 text-xs text-[var(--muted)]">사진 첨부됨</div>
                  <button
                    type="button"
                    onClick={() => setScreenshotUrl(null)}
                    className="rounded-full p-1.5 text-[var(--muted)] active:text-[var(--foreground)]"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-[var(--border)] py-3 text-sm text-[var(--muted)] active:bg-[var(--surface-2)]"
                >
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
                  {uploading ? "업로드 중…" : "사진 첨부 (선택, 최대 20MB)"}
                </button>
              )}
            </div>

            {error && <div className="mt-3 text-sm text-red-400">{error}</div>}
          </div>

          <div
            className="px-5 pb-4"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)" }}
          >
            <button
              type="button"
              onClick={send}
              disabled={!canSend}
              className={cn(
                "h-14 w-full rounded-2xl text-base font-semibold transition",
                canSend
                  ? "bg-[var(--accent)] text-white active:opacity-80"
                  : "bg-[var(--surface-2)] text-[var(--muted)]",
              )}
            >
              {saving ? "보내는 중…" : "제보 보내기"}
            </button>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
